import { getBaseBuilderVersion } from '../helpers/utils'
import Attrs from '../models/Attrs'
import type { Flow, FlowPage, ProgressBar } from '../models/types'
import { generateElementSelectors } from '../styles/selectors'

const CONTROLLER = 'components--progress-bar'
export default function renderProgressBar(
  component: ProgressBar,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow
): string {
  const barType = component.progress_type || 'circle'

  attrs
    .addController(CONTROLLER)
    .addDOMClass('FormProgress')
    .addAction(`${CONTROLLER}:reset->flow#reset`)
    .addValueIfExists(CONTROLLER, 'percent-type', component.percentageType)
    .addValueIfExists(CONTROLLER, 'start-at-one', `${!!component.progressBarStartAtOne}`)
    .addValue(CONTROLLER, 'bar-type', barType)

  if (component.showProgressPercentage)
    attrs.addDOMClass(component.progressBarLabelPosition || 'left')
  if (component.page_reactive_classes) attrs.addReactiveElement()

  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  switch (barType) {
    case 'simple': {
      attrs.addDOMClass(
        generateElementSelectors({
          elementType: 'ProgressBarBackground',
          component,
          page,
          builderVersion,
        })
      )
      if (
        component.percentageType === 'fractional' &&
        component.progressBarStartAtOne &&
        component.progressBarCompleteText
      )
        attrs.addValueIfExists(CONTROLLER, 'complete-text', component.progressBarCompleteText)

      const barAttrs = new Attrs(attrs.static_render).addTarget(CONTROLLER, 'bar').addDOMClass([
        'bar',
        ...generateElementSelectors({
          elementType: 'ProgressBarFilled',
          component,
          page,
          builderVersion,
        }),
      ])
      if (component.page_reactive_classes) barAttrs.addReactiveElement()
      const iconAttrs = new Attrs(attrs.static_render)
        .addTarget(CONTROLLER, 'reset')
        .addDOMClass('reset')
        .addIcon('fa-solid', 'redo')
        .addAction(`click->${CONTROLLER}#resetUserData`)
      if (component.page_reactive_classes) iconAttrs.addReactiveElement()

      const textAttrs = new Attrs(attrs.static_render)
        .addTarget(CONTROLLER, 'text')
        .addDOMClass('progress')
      if (component.page_reactive_classes) textAttrs.addReactiveElement()
      const bar = `<div ${barAttrs}></div>`
      const barText = component.showProgressPercentage ? `<div ${textAttrs}></div>` : ''

      return `<div ${attrs}>${bar}${barText}<div ${iconAttrs}></div></div>`
    }
    case 'circle': {
      const size = 30
      const halfSize = size / 2
      const radius = size / 2 - 1.8

      attrs
        .addDOMClass('FormProgressCircle')
        .addValue(CONTROLLER, 'size', `${size}`)
        .addValue(CONTROLLER, 'bar-color', '#0575FF')
        .addAction(`click->${CONTROLLER}#resetUserData`)

      const svgAttrs = new Attrs(attrs.static_render)
        .add('height', `${size}`)
        .add('width', `${size}`)
        .add('xmlns', 'http://www.w3.org/2000/svg')
        .addDOMClass('ep-svg')
        .addTarget(CONTROLLER, 'svg')

      const transparentCircleAttrs = new Attrs(attrs.static_render)
        .add('r', `${radius}`)
        .add('cx', `${halfSize}`)
        .add('cy', `${halfSize}`)
        .add('fill', 'transparent')
        .add('stroke', '#e6e9f0')
        .add('stroke-width', '1.5')
        .add('stroke-dasharray', '')
        .add('style', 'transition-duration: 1000ms; transition-timing-function: ease-in-out')
        .addDOMClass([
          ...generateElementSelectors({
            elementType: 'ProgressBarBackground',
            component,
            page,
            builderVersion,
          }),
          'ep-circle--empty',
        ])
        .addTarget(CONTROLLER, 'transparentCircle')
      const fillCircleAttrs = new Attrs(attrs.static_render)
        .add('r', `${radius}`)
        .add('cx', `${halfSize}`)
        .add('cy', `${halfSize}`)
        .add('fill', 'transparent')
        .add('stroke-width', '3.6')
        .add('stroke', '#0575FF')
        .add('stroke-dasharray', '99')
        .add('stroke-linecap', 'round')
        .add(
          'style',
          `transition: all 1000ms ease-in-out 0s, opacity 0.3s 0s;stroke-dashoffset: 76.303;transform-origin: 50% 50%;opacity: 1;--ep-circumference: 82.938;--ep-negative-circumference: -82.938;--ep-double-circumference: 165.876;--ep-stroke-offset: 76.303;--ep-loop-stroke-offset: -89.5731;--ep-bounce-out-stroke-offset: 0;--ep-bounce-in-stroke-offset: 76.303;--ep-reverse-stroke-offset: 242.179;--ep-loading-stroke-offset: 16.5876;animation-duration: ${
            component.animate_in ? '1000ms' : '0ms'
          };`
        )
        .addDOMClass([
          ...generateElementSelectors({
            elementType: 'ProgressBarFilled',
            component,
            page,
            builderVersion,
          }),
          'ep-circle--progress',
          'animation__default',
        ])
        .addTarget(CONTROLLER, 'fillCircle')

      if (component.page_reactive_classes) {
        transparentCircleAttrs.addReactiveElement()
        fillCircleAttrs.addReactiveElement()
      }

      const legendAttrs = new Attrs(attrs.static_render).addTarget(CONTROLLER, 'text')
      const legend = component.showProgressPercentage ? `<div ${legendAttrs}></div>` : ''

      return `<div ${attrs}><svg ${svgAttrs}>
      <g class="ep-circle--container">
        <g class="ep-circle" style="transition-timing-function: ease-in-out; transform: rotate(-90deg)">
          <circle ${transparentCircleAttrs}></circle>
          <circle ${fillCircleAttrs}></circle>
        </g>
      </g>
      </svg>${legend}
      </div>`
    }
    case 'custom':
    default:
      return `<div ${attrs}>Type: ${barType} ProgressBar </div>`
  }
}
